import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft, MapPin, Search, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Spinner } from '@/components/ui/spinner'
import { jobsApi } from '@/services/jobs'
import { categoriesApi } from '@/services/categories'
import { locationsApi } from '@/services/locations'
import { workersApi } from '@/services/workers'
import { orgUsersApi, type OrgUser } from '@/services/org-users'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { useAuthStore } from '@/stores/auth-store'
import type { Job, Worker } from '@/types'
import { getInitials, cn } from '@/lib/utils'

const recurrenceRules = ['daily', 'weekly', 'bi_weekly', 'monthly', 'every_n_days', 'weekly_days', 'monthly_day'] as const

const recurringJobSchema = z.object({
  title: z.string().trim().optional(),
  category: z.string().trim().min(1, 'Category is required'),
  client_user: z.string().optional(),
  location_id: z.string().optional(),
  status: z.enum(['draft', 'scheduled', 'in_progress', 'completed', 'cancelled', 'on_hold']).default('draft'),
  priority: z.enum(['low', 'medium', 'high', 'urgent', 'critical']).default('medium'),
  recurrence_rule: z.enum(recurrenceRules).default('weekly_days'),
  recurrence_interval_days: z.string().optional(),
  recurrence_weekdays: z.array(z.number().int().min(0).max(6)).default([1, 2, 3, 4, 5]),
  recurrence_month_day: z.string().optional(),
  recurrence_count: z.string().optional(),
  recurrence_end: z.string().optional(),
  start_date: z.string().trim().min(1, 'Start date is required'),
  start_time: z.string().trim().min(1, 'Start time is required'),
  instructions: z.string().trim().optional(),
  primary_worker: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.recurrence_rule === 'every_n_days') {
    const interval = Number(data.recurrence_interval_days)
    if (!Number.isFinite(interval) || interval <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter the number of days between recurring jobs.',
        path: ['recurrence_interval_days'],
      })
    }
  }
  if (data.recurrence_rule === 'weekly_days' && data.recurrence_weekdays.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Select at least one weekday.',
      path: ['recurrence_weekdays'],
    })
  }
  if (data.recurrence_rule === 'monthly_day') {
    const day = Number(data.recurrence_month_day)
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a day of month between 1 and 31.',
        path: ['recurrence_month_day'],
      })
    }
  }
  if (data.recurrence_rule !== 'daily' && data.recurrence_count) {
    const count = Number(data.recurrence_count)
    if (!Number.isInteger(count) || count <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Occurrences must be a positive number.',
        path: ['recurrence_count'],
      })
    }
  }
})

type RecurringJobFormData = z.infer<typeof recurringJobSchema>

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const FIELD_LABELS: Partial<Record<keyof RecurringJobFormData, string>> = {
  category: 'Category',
  client_user: 'Customer',
  location_id: 'Customer location',
  title: 'Job title',
  recurrence_rule: 'Recurrence type',
  recurrence_interval_days: 'Repeat every',
  recurrence_month_day: 'Month day',
  recurrence_weekdays: 'Weekdays',
  recurrence_count: 'Occurrences',
  recurrence_end: 'Repeat until',
  start_date: 'Start date',
  start_time: 'Start time',
  status: 'Status',
  priority: 'Priority',
  primary_worker: 'Worker',
}

const formatWorkerName = (worker: Worker) => {
  const name = [worker.first_name, worker.last_name]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim()

  return name || worker.display_name || worker.worker_name || `Worker ${worker.id}`
}

const HOUR_IN_MS = 60 * 60 * 1000

const toLocalDateTime = (dateValue?: string, timeValue?: string) => {
  if (!dateValue || !timeValue) return null
  const candidate = new Date(`${dateValue}T${timeValue}:00`)
  if (Number.isNaN(candidate.getTime())) return null
  return candidate
}

const toDateOnly = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const toTimeLabel = (value: Date) => value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const parseDateTimeOrNull = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

const assignmentStatusFromLoad = (percent: number) => {
  if (percent >= 70) return 'available'
  if (percent >= 40) return 'limited'
  return 'busy'
}

const workerRole = (worker: Worker) => worker.role_name || worker.linked_post_role_name || 'Worker'

const getCustomerOptionValue = (client: OrgUser) => String(client.user_id ?? client.id)

const getCustomerDisplayName = (client: OrgUser) => {
  const name = [client.fname, client.lname]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim()

  const baseName = name || String(client.email ?? '').trim() || `Customer ${client.id}`
  const regNum = String(client.usr_reg_num ?? '').trim()
  if (!regNum || regNum === '0') {
    return baseName
  }

  return `${baseName} (${regNum})`
}

const normalizeCustomerGroupKey = (value?: string | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

const isCustomerGroupUser = (user: OrgUser) => {
  const codeKey = normalizeCustomerGroupKey(user.user_group_code)
  const titleKey = normalizeCustomerGroupKey(user.user_group_title)
  const allowed = new Set(['retail_customer', 'business_customer', 'customer', 'client'])
  return allowed.has(codeKey) || allowed.has(titleKey)
}

export default function RecurringJobCreatePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || storedServiceInstanceId

  const {
    data: categoriesResponse,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    error: categoriesError,
  } = useQuery({
    queryKey: ['recurring-job-categories', serviceInstanceId ?? 'none'],
    queryFn: () => categoriesApi.getCategories({
      kind: 'job',
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const { data: locationsResponse = [], isLoading: isLocationsLoading } = useQuery({
    queryKey: ['recurring-job-locations', serviceInstanceId ?? 'none'],
    queryFn: () => locationsApi.list({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const { data: workersResponse, isLoading: isWorkersLoading } = useQuery({
    queryKey: ['recurring-job-workers', serviceInstanceId ?? 'none'],
    queryFn: () => workersApi.getWorkers({
      page_size: 100,
      active: true,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 30_000,
  })

  const categories = categoriesResponse?.results ?? []
  const locations = locationsResponse
  const workers = workersResponse?.results ?? []

  const [workerSearch, setWorkerSearch] = useState('')
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'busy'>('all')
  const [selectedWorkerIds, setSelectedWorkerIds] = useState<string[]>([])
  const [customerSearch, setCustomerSearch] = useState('')
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('')
  const [resolvedSelectedClient, setResolvedSelectedClient] = useState<OrgUser | null>(null)
  const todayDateOnly = useMemo(() => toDateOnly(new Date()), [])

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RecurringJobFormData>({
    resolver: zodResolver(recurringJobSchema),
    defaultValues: {
      title: '',
      category: '',
      client_user: 'none',
      location_id: 'none',
      status: 'draft',
      priority: 'medium',
      recurrence_rule: 'weekly_days',
      recurrence_interval_days: '',
      recurrence_weekdays: [1, 2, 3, 4, 5],
      recurrence_month_day: '',
      recurrence_count: '',
      recurrence_end: '',
      start_date: todayDateOnly,
      start_time: '09:00',
      instructions: '',
      primary_worker: '',
    },
  })

  const selectedCategory = watch('category')
  const recurrenceRule = watch('recurrence_rule')
  const selectedWeekdays = watch('recurrence_weekdays')
  const recurrenceIntervalDays = watch('recurrence_interval_days')
  const recurrenceMonthDay = watch('recurrence_month_day')
  const recurrenceCount = watch('recurrence_count')
  const startDate = watch('start_date')
  const startTime = watch('start_time')
  const recurrenceEnd = watch('recurrence_end')
  const selectedLocationId = watch('location_id')
  const selectedClientUser = watch('client_user')
  const selectedCustomerUser = selectedClientUser && selectedClientUser !== 'none' ? selectedClientUser : undefined

  const timelineWindow = useMemo(() => {
    const start = toLocalDateTime(startDate, startTime)
    if (!start) {
      return null
    }
    const end = new Date(start.getTime() + 24 * HOUR_IN_MS)
    const slots = Array.from({ length: 24 }, (_, index) => ({
      index,
      start: new Date(start.getTime() + index * HOUR_IN_MS),
      end: new Date(start.getTime() + (index + 1) * HOUR_IN_MS),
    }))

    return {
      start,
      end,
      startDateOnly: toDateOnly(start),
      endDateOnly: toDateOnly(end),
      slots,
    }
  }, [startDate, startTime])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedCustomerSearch(customerSearch.trim())
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [customerSearch])

  const { data: clientsPage, isLoading: isClientsLoading, isFetching: isClientsFetching } = useQuery({
    queryKey: ['recurring-job-clients', serviceInstanceId ?? 'none', debouncedCustomerSearch],
    queryFn: () => orgUsersApi.listPaginated({
      search: debouncedCustomerSearch,
      serviceInstanceId,
      page: 1,
      pageSize: 200,
      includeCustomerActivity: true,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 20_000,
  })

  const { data: jobsWindowResponse, isLoading: isJobsWindowLoading } = useQuery({
    queryKey: [
      'recurring-job-workers-window-jobs',
      serviceInstanceId ?? 'none',
      timelineWindow?.startDateOnly ?? 'none',
      timelineWindow?.endDateOnly ?? 'none',
    ],
    queryFn: () => jobsApi.getJobs({
      service_instance_id: serviceInstanceId ?? undefined,
      date_filter: 'custom',
      start_date: timelineWindow?.startDateOnly,
      end_date: timelineWindow?.endDateOnly,
      page_size: 400,
      ordering: 'scheduled_start',
    }),
    enabled: Boolean(serviceInstanceId && timelineWindow?.startDateOnly && timelineWindow?.endDateOnly),
    staleTime: 20_000,
  })

  useEffect(() => { setValue('primary_worker', selectedWorkerIds.length ? selectedWorkerIds[0] : '', { shouldDirty: true }) }, [selectedWorkerIds, setValue])

  const clients = clientsPage?.results ?? []

  useEffect(() => {
    if (!selectedClientUser || selectedClientUser === 'none') {
      setResolvedSelectedClient(null)
      return
    }

    const matchedClient = clients.find((item) => getCustomerOptionValue(item) === selectedClientUser)
    if (matchedClient) {
      if (isCustomerGroupUser(matchedClient)) {
        setResolvedSelectedClient(matchedClient)
      } else {
        setResolvedSelectedClient(null)
        setValue('client_user', 'none', { shouldDirty: true })
      }
      return
    }

    let cancelled = false

    void orgUsersApi.getClient(selectedClientUser).then((client) => {
      if (!cancelled) {
        if (isCustomerGroupUser(client)) {
          setResolvedSelectedClient(client)
        } else {
          setResolvedSelectedClient(null)
          setValue('client_user', 'none', { shouldDirty: true })
        }
      }
    }).catch(() => {
      // Ignore lookup failures for now.
    })

    return () => {
      cancelled = true
    }
  }, [clients, selectedClientUser, setValue])

  const categoryOptions = useMemo(() => categories.map((category) => ({
    value: String(category.id),
    label: category.name,
    keywords: [category.name, category.slug, category.kind].filter(Boolean).join(' '),
  })), [categories])

  const customerScopedLocations = useMemo(() => {
    if (!selectedCustomerUser) {
      return []
    }

    return locations.filter((savedLocation) => {
      const directCustomerId = String(savedLocation.client_user ?? '').trim()
      const infoCustomerId = String(savedLocation.client_user_info?.id ?? '').trim()
      return directCustomerId === selectedCustomerUser || infoCustomerId === selectedCustomerUser
    })
  }, [locations, selectedCustomerUser])

  const locationOptions = useMemo(() => [
    {
      value: 'none',
      label: selectedCustomerUser ? 'No customer location selected' : 'Select customer first',
      keywords: 'none no customer location selected',
    },
    ...customerScopedLocations.map((savedLocation) => ({
      value: String(savedLocation.id),
      label: savedLocation.place,
      keywords: [savedLocation.place, savedLocation.address].filter(Boolean).join(' '),
    })),
  ], [customerScopedLocations, selectedCustomerUser])

  useEffect(() => {
    if (!selectedLocationId || selectedLocationId === 'none') {
      return
    }

    const existsInScopedLocations = customerScopedLocations.some((entry) => String(entry.id) === selectedLocationId)
    if (!existsInScopedLocations) {
      setValue('location_id', 'none', { shouldDirty: true })
    }
  }, [customerScopedLocations, selectedLocationId, setValue])

  const clientOptions = useMemo(() => {
    const options = clients.filter(isCustomerGroupUser).map((client) => ({
      value: getCustomerOptionValue(client),
      label: getCustomerDisplayName(client),
      keywords: [client.fname, client.lname, client.email, client.phone, client.username, client.usr_reg_num].filter(Boolean).join(' '),
    }))

    if (
      resolvedSelectedClient
      && !options.some((option) => option.value === getCustomerOptionValue(resolvedSelectedClient))
    ) {
      options.unshift({
        value: getCustomerOptionValue(resolvedSelectedClient),
        label: getCustomerDisplayName(resolvedSelectedClient),
        keywords: [
          resolvedSelectedClient.fname,
          resolvedSelectedClient.lname,
          resolvedSelectedClient.email,
          resolvedSelectedClient.phone,
          resolvedSelectedClient.username,
          resolvedSelectedClient.usr_reg_num,
        ].filter(Boolean).join(' '),
      })
    }

    return [
      { value: 'none', label: 'No linked customer', keywords: 'none unlinked' },
      ...options,
    ]
  }, [clients, resolvedSelectedClient])

  const filteredWorkers = useMemo(() => {
    const normalizedSearch = workerSearch.trim().toLowerCase()
    return workers.filter((worker) => {
      if (selectedCategory) {
        const categoryMatch = Array.isArray(worker.categories)
          && worker.categories.some((entry) => String(entry) === String(selectedCategory))
        if (!categoryMatch) {
          return false
        }
      }

      if (!normalizedSearch) {
        return true
      }

      const haystack = [
        formatWorkerName(worker),
        workerRole(worker),
        worker.email,
        worker.phone,
        worker.phone_number,
      ].filter(Boolean).join(' ').toLowerCase()

      return haystack.includes(normalizedSearch)
    })
  }, [selectedCategory, workerSearch, workers])

  const jobsInWindow = jobsWindowResponse?.results ?? []

  const workerMetrics = useMemo(() => {
    const metricsByWorker = new Map<string, {
      occupiedSlots: boolean[]
      assignedJobs: Job[]
      assignedHours: number
      availabilityPercent: number
      status: 'available' | 'limited' | 'busy'
    }>()

    if (!timelineWindow) {
      return metricsByWorker
    }

    const workerIdAliases = new Map<string, Set<string>>()
    workers.forEach((worker) => {
      const aliases = new Set<string>([
        String(worker.id),
        String(worker.worker_id ?? ''),
        String(worker.user_id ?? ''),
      ].filter((value) => value.trim().length > 0))
      workerIdAliases.set(String(worker.id), aliases)
    })

    const defaultSlotState = () => Array.from({ length: 24 }, () => false)

    workers.forEach((worker) => {
      metricsByWorker.set(String(worker.id), {
        occupiedSlots: defaultSlotState(),
        assignedJobs: [],
        assignedHours: 0,
        availabilityPercent: worker.status === 'active' ? 100 : 35,
        status: worker.status === 'active' ? 'available' : 'busy',
      })
    })

    const rangeStart = timelineWindow.start.getTime()
    const rangeEnd = timelineWindow.end.getTime()

    jobsInWindow.forEach((job) => {
      const jobAssigneeCandidates = new Set<string>([
        String(job.primary_worker ?? ''),
        String(job.primary_worker_info?.id ?? ''),
      ].filter((value) => value.trim().length > 0))
      if (jobAssigneeCandidates.size === 0) {
        return
      }

      const start = parseDateTimeOrNull(job.scheduled_start || job.start_date)
      if (!start) {
        return
      }

      const parsedEnd = parseDateTimeOrNull(job.scheduled_end || job.end_date)
      const fallbackEnd = new Date(start.getTime() + HOUR_IN_MS)
      const end = parsedEnd && parsedEnd.getTime() > start.getTime() ? parsedEnd : fallbackEnd

      const jobStartTime = Math.max(start.getTime(), rangeStart)
      const jobEndTime = Math.min(end.getTime(), rangeEnd)
      if (jobEndTime <= jobStartTime) {
        return
      }

      workers.forEach((worker) => {
        const aliases = workerIdAliases.get(String(worker.id))
        if (!aliases) {
          return
        }

        const isAssignedToWorker = Array.from(jobAssigneeCandidates).some((candidate) => aliases.has(candidate))
        if (!isAssignedToWorker) {
          return
        }

        const existing = metricsByWorker.get(String(worker.id))
        if (!existing) {
          return
        }

        existing.assignedJobs.push(job)
        const overlapHours = (jobEndTime - jobStartTime) / HOUR_IN_MS
        existing.assignedHours += Math.max(0, overlapHours)

        timelineWindow.slots.forEach((slot, index) => {
          const slotStart = slot.start.getTime()
          const slotEnd = slot.end.getTime()
          if (jobStartTime < slotEnd && jobEndTime > slotStart) {
            existing.occupiedSlots[index] = true
          }
        })
      })
    })

    metricsByWorker.forEach((value, workerId) => {
      const occupiedHours = value.occupiedSlots.filter(Boolean).length
      const percent = Math.max(0, Math.min(100, Math.round(((24 - occupiedHours) / 24) * 100)))
      value.availabilityPercent = percent
      value.status = assignmentStatusFromLoad(percent)
      value.assignedJobs.sort((a, b) => {
        const aTime = parseDateTimeOrNull(a.scheduled_start || a.start_date)?.getTime() ?? 0
        const bTime = parseDateTimeOrNull(b.scheduled_start || b.start_date)?.getTime() ?? 0
        return aTime - bTime
      })
      metricsByWorker.set(workerId, value)
    })

    return metricsByWorker
  }, [jobsInWindow, timelineWindow, workers])

  const visibleWorkers = useMemo(() => {
    return filteredWorkers.filter((worker) => {
      if (availabilityFilter === 'all') {
        return true
      }

      const metric = workerMetrics.get(String(worker.id))
      if (!metric) {
        return availabilityFilter !== 'available'
      }

      if (availabilityFilter === 'available') {
        return metric.status === 'available'
      }

      return metric.status !== 'available'
    })
  }, [availabilityFilter, filteredWorkers, workerMetrics])

  const recurrenceSummary = useMemo(() => {
    if (recurrenceRule === 'every_n_days' && recurrenceIntervalDays) {
      return `Every ${recurrenceIntervalDays} days`
    }
    if (recurrenceRule === 'weekly_days') {
      const labels = selectedWeekdays.map((day) => WEEKDAY_LABELS[day]).filter(Boolean)
      return labels.length > 0 ? `Weekly on ${labels.join(', ')}` : 'Weekly on selected days'
    }
    if (recurrenceRule === 'monthly_day' && recurrenceMonthDay) {
      return `Monthly on day ${recurrenceMonthDay}`
    }
    if (recurrenceRule === 'bi_weekly') {
      return 'Every two weeks'
    }
    if (recurrenceRule === 'daily') {
      return 'Daily'
    }
    return 'Recurring job'
  }, [recurrenceIntervalDays, recurrenceMonthDay, recurrenceRule, selectedWeekdays])

  const recurrenceCountLabel = recurrenceCount
    ? `${recurrenceCount} occurrences`
    : 'Open-ended'

  const selectedLocation = customerScopedLocations.find((entry) => String(entry.id) === selectedLocationId)

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof jobsApi.create>[0]) => jobsApi.create(payload),
    onSuccess: async (createdJob: Job) => {
      await queryClient.invalidateQueries({ queryKey: ['jobs'] })
      toast.success('Recurring job created successfully')
      navigate(withServiceInstanceId(`/job/${createdJob.id}`, serviceInstanceId))
    },
    onError: (error) => {
      console.error('[RecurringJobCreate] Create recurring job API call failed:', error)
      toast.error(getErrorMessage(error))
    },
  })

  const categoryLoadMessage = useMemo(() => {
    if (!serviceInstanceId) {
      return 'Missing service instance context. Open this page from a branch or service workspace.'
    }

    if (isCategoriesLoading) {
      return 'Loading job categories before recurring jobs can be created.'
    }

    if (isCategoriesError) {
      return getErrorMessage(categoriesError)
    }

    if (categories.length === 0) {
      return 'No job categories are available for this service instance. Add a job category first.'
    }

    return null
  }, [categories.length, categoriesError, isCategoriesError, isCategoriesLoading, serviceInstanceId])

  const isSubmitDisabled = Boolean(
    createMutation.isPending
    || !serviceInstanceId
    || isCategoriesLoading
    || isCategoriesError
    || categories.length === 0,
  )

  const onInvalid = (formErrors: FieldErrors<RecurringJobFormData>) => {
    const invalidFields = Object.keys(formErrors) as (keyof RecurringJobFormData)[]
    console.warn('[RecurringJobCreate] Submission blocked by validation:', formErrors)

    const messages = invalidFields.map((field) => {
      const label = FIELD_LABELS[field] ?? field
      const message = formErrors[field]?.message
      return message ? `${label}: ${message}` : label
    })
    toast.error('Please fix the highlighted fields before creating the recurring job.', {
      description: messages.join(' • '),
    })

    const firstField = invalidFields[0]
    if (firstField) {
      const element = document.getElementById(firstField)
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      element?.focus({ preventScroll: true })
    }
  }

  const onSubmit = (data: RecurringJobFormData) => {
    try {
      const hasValidClientSelection = Boolean(
        resolvedSelectedClient
        && data.client_user
        && data.client_user !== 'none'
        && isCustomerGroupUser(resolvedSelectedClient),
      )

      // Backend Job.primary_worker is a FK to Worker (integer PK), so send a number.
      const primaryWorkerRaw = selectedWorkerIds[0] || data.primary_worker
      const primaryWorkerId = primaryWorkerRaw ? Number(primaryWorkerRaw) : undefined

      const payload: Parameters<typeof jobsApi.create>[0] = {
        title: data.title?.trim() || 'Recurring job',
        service_instance_id: serviceInstanceId ?? undefined,
        category: data.category,
        client_user: hasValidClientSelection ? data.client_user : undefined,
        client_name: hasValidClientSelection && resolvedSelectedClient ? getCustomerDisplayName(resolvedSelectedClient) : undefined,
        location_id: data.location_id && data.location_id !== 'none' ? data.location_id : undefined,
        location: selectedLocation?.place || undefined,
        city: selectedLocation?.address || undefined,
        status: data.status,
        priority: data.priority,
        description: data.instructions?.trim() || undefined,
        primary_worker: primaryWorkerId !== undefined && Number.isFinite(primaryWorkerId) ? primaryWorkerId : undefined,
        recurrence_rule: data.recurrence_rule,
        recurrence_interval_days: data.recurrence_rule === 'every_n_days' && data.recurrence_interval_days
          ? Number(data.recurrence_interval_days)
          : undefined,
        recurrence_weekdays: data.recurrence_rule === 'weekly_days' ? data.recurrence_weekdays : [],
        recurrence_month_day: data.recurrence_rule === 'monthly_day' && data.recurrence_month_day
          ? Number(data.recurrence_month_day)
          : undefined,
        recurrence_count: data.recurrence_rule !== 'daily' && data.recurrence_count
          ? Number(data.recurrence_count)
          : undefined,
        recurrence_end: data.recurrence_rule !== 'daily' && data.recurrence_end ? data.recurrence_end : undefined,
        scheduled_start: data.start_date && data.start_time ? `${data.start_date}T${data.start_time}:00` : undefined,
      }

      console.log('[RecurringJobCreate] Calling jobsApi.create (POST /Job/Add/) with payload:', payload)
      createMutation.mutate(payload)
    } catch (error) {
      console.error('[RecurringJobCreate] Failed to build recurring job payload:', error)
      toast.error('Could not prepare the recurring job request. Please check the form and try again.')
    }
  }

  // Worker ids arrive as numbers at runtime despite the `string` type; keep form state as strings.
  const toggleWorker = (workerId: string | number) => {
    const normalizedId = String(workerId)
    setSelectedWorkerIds((previous) => (
      previous.includes(normalizedId)
        ? previous.filter((entry) => entry !== normalizedId)
        : [...previous, normalizedId]
    ))
  }

  const selectSuggestedWorkers = () => {
    setSelectedWorkerIds(visibleWorkers.slice(0, 3).map((worker) => String(worker.id)))
  }

  const totalSelectedWorkers = selectedWorkerIds.length

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 lg:px-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Recurring job screen
          </h1>
          <p className="text-sm text-muted-foreground">
            Plan repeating jobs, preview the cadence, and pick the right worker team before you save.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="border-border bg-card text-foreground hover:bg-accent"
          onClick={() => navigate(withServiceInstanceId('/job', serviceInstanceId))}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {['Details', 'Recurrence', 'Workers', 'Finalize'].map((label, index) => (
          <div
            key={label}
            className={cn(
              'rounded-2xl border px-4 py-3 text-sm font-medium shadow-sm transition-all',
              index === 0
                ? 'border-primary/30 bg-primary/5 text-primary'
                : 'border-border bg-card text-muted-foreground hover:border-primary/20 hover:bg-accent/40',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span>{label}</span>
              <span className={cn('rounded-full px-2 py-0.5 text-xs', index === 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
                0{index + 1}
              </span>
            </div>
          </div>
        ))}
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        <Card className="overflow-hidden border-border bg-card shadow-sm">
          <CardHeader className="space-y-1 border-b border-border bg-muted/30">
            <CardTitle className="text-base text-foreground">Details</CardTitle>
            <CardDescription className="text-muted-foreground">Capture the job basics and where it will happen.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {categoryLoadMessage ? (
              <div
                className={cn(
                  'rounded-xl border px-4 py-3 text-sm',
                  isCategoriesError || !serviceInstanceId || categories.length === 0
                    ? 'border-amber-200 bg-amber-50 text-amber-900'
                    : 'border-sky-200 bg-sky-50 text-sky-900',
                )}
              >
                {categoryLoadMessage}
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    <SearchableSelect
                      id="category"
                      ariaLabel="Category"
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      options={categoryOptions}
                      placeholder={isCategoriesLoading ? 'Loading categories...' : 'Search or select category'}
                      searchPlaceholder="Type a category name"
                      emptyText="No categories found."
                      emptySearchText="Start typing a category name."
                      disabled={isCategoriesLoading || categories.length === 0}
                    />
                  )}
                />
                {errors.category ? <p className="text-sm text-destructive">{errors.category.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_user">Customer</Label>
                <Controller
                  control={control}
                  name="client_user"
                  render={({ field }) => (
                    <SearchableSelect
                      id="client_user"
                      ariaLabel="Customer"
                      value={field.value || 'none'}
                      onValueChange={field.onChange}
                      options={clientOptions}
                      placeholder={isClientsLoading ? 'Loading customers...' : 'Search or select customer'}
                      searchPlaceholder="Search customers"
                      emptyText="No customers found."
                      onSearchChange={setCustomerSearch}
                      filterLocally={false}
                      disabled={isClientsLoading && !isClientsFetching}
                    />
                  )}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="location_id">Customer location</Label>
                <Controller
                  control={control}
                  name="location_id"
                  render={({ field }) => (
                    <SearchableSelect
                      id="location_id"
                      ariaLabel="Customer location"
                      value={field.value || 'none'}
                      onValueChange={field.onChange}
                      options={locationOptions}
                      placeholder={
                        !selectedCustomerUser
                          ? 'Select customer first'
                          : isLocationsLoading
                            ? 'Loading customer locations...'
                            : 'Select customer location'
                      }
                      searchPlaceholder="Search locations"
                      emptyText={selectedCustomerUser ? 'No locations linked to this customer.' : 'Select customer first.'}
                      disabled={isLocationsLoading || !selectedCustomerUser}
                    />
                  )}
                />
                {!selectedCustomerUser ? <p className="text-xs text-muted-foreground">Pick a customer to load linked locations.</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Job title (optional)</Label>
                <Input id="title" placeholder="Weekly house cleaning" {...register('title')} aria-invalid={!!errors.title} />
                {errors.title ? <p className="text-sm text-destructive">{errors.title.message}</p> : null}
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Location details</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedLocation?.place || 'Select a customer location.'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-border bg-card shadow-sm">
          <CardHeader className="space-y-1 border-b border-border bg-muted/30">
            <CardTitle className="text-base text-foreground">Recurrence</CardTitle>
            <CardDescription className="text-muted-foreground">Define how often this job should repeat.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="recurrence_rule">Recurrence type</Label>
                <Controller
                  control={control}
                  name="recurrence_rule"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="recurrence_rule">
                        <SelectValue placeholder="Select recurrence" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="every_n_days">Every N days</SelectItem>
                        <SelectItem value="weekly_days">Selected weekdays</SelectItem>
                        <SelectItem value="monthly_day">Specific day of month</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recurrence_interval_days">Repeat every</Label>
                <Input
                  id="recurrence_interval_days"
                  type="number"
                  min="1"
                  placeholder="Days"
                  {...register('recurrence_interval_days')}
                  disabled={recurrenceRule !== 'every_n_days'}
                />
                {errors.recurrence_interval_days ? <p className="text-sm text-destructive">{errors.recurrence_interval_days.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="recurrence_month_day">Month day</Label>
                <Input
                  id="recurrence_month_day"
                  type="number"
                  min="1"
                  max="31"
                  placeholder="15"
                  {...register('recurrence_month_day')}
                  disabled={recurrenceRule !== 'monthly_day'}
                />
                {errors.recurrence_month_day ? <p className="text-sm text-destructive">{errors.recurrence_month_day.message}</p> : null}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Weekdays</Label>
              <Controller
                control={control}
                name="recurrence_weekdays"
                render={({ field }) => (
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAY_LABELS.map((label, dayIndex) => {
                      const checked = field.value.includes(dayIndex)
                      return (
                        <Button
                          key={label}
                          type="button"
                          variant={checked ? 'default' : 'outline'}
                          size="sm"
                          className={cn(checked ? 'bg-indigo-600 hover:bg-indigo-700' : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50')}
                          onClick={() => field.onChange(checked ? field.value.filter((day) => day !== dayIndex) : [...field.value, dayIndex].sort((a, b) => a - b))}
                        >
                          {label}
                        </Button>
                      )
                    })}
                  </div>
                )}
              />
              {errors.recurrence_weekdays ? <p className="text-sm text-destructive">{errors.recurrence_weekdays.message}</p> : null}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start date</Label>
                <Input id="start_date" type="date" {...register('start_date')} aria-invalid={!!errors.start_date} />
                {errors.start_date ? <p className="text-sm text-destructive">{errors.start_date.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_time">Start time</Label>
                <Input id="start_time" type="time" {...register('start_time')} aria-invalid={!!errors.start_time} />
                {errors.start_time ? <p className="text-sm text-destructive">{errors.start_time.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="recurrence_end">Repeat until</Label>
                <Input id="recurrence_end" type="date" {...register('recurrence_end')} />
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-muted/40 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-primary">Schedule preview</p>
              <p className="mt-1 text-sm font-medium text-foreground">{recurrenceSummary}</p>
              <p className="text-sm text-muted-foreground">
                {startDate && startTime ? `Starts ${startDate} at ${startTime}` : 'Pick a start date and time to preview the schedule.'}
              </p>
              <p className="text-sm text-muted-foreground">
                {recurrenceEnd ? `Repeat until ${recurrenceEnd}` : recurrenceCountLabel}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-border bg-card shadow-sm">
          <CardHeader className="space-y-1 border-b border-border bg-muted/30">
            <CardTitle className="text-base text-foreground">Worker selection</CardTitle>
            <CardDescription className="text-muted-foreground">Choose the workers that should receive the recurring assignment.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search workers"
                  value={workerSearch}
                  onChange={(event) => setWorkerSearch(event.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Select value={availabilityFilter} onValueChange={(value) => setAvailabilityFilter(value as typeof availabilityFilter)}>
                  <SelectTrigger className="w-[170px]">
                    <SelectValue placeholder="Availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All workers</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="busy">Busy / limited</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" className="border-border text-foreground hover:bg-accent" onClick={selectSuggestedWorkers}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Auto assign
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border">
              <div className="grid grid-cols-[44px_1.2fr_0.7fr_0.6fr_1.5fr] gap-3 border-b border-border bg-muted/50 px-4 py-3 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                <div />
                <div>Worker</div>
                <div>Role</div>
                <div>Assigned</div>
                <div>24h timeline</div>
              </div>
              <div className="divide-y divide-border">
                {isWorkersLoading || (timelineWindow && isJobsWindowLoading) ? (
                  <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                    <Spinner className="mr-2" /> Loading workers and assignments...
                  </div>
                ) : visibleWorkers.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-muted-foreground">No workers matched your filters.</div>
                ) : (
                  visibleWorkers.slice(0, 8).map((worker) => {
                    const metrics = workerMetrics.get(String(worker.id))
                    const availability = metrics?.availabilityPercent ?? (worker.status === 'active' ? 100 : 35)
                    const selected = selectedWorkerIds.includes(String(worker.id))
                    return (
                      <div
                        key={worker.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleWorker(worker.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            toggleWorker(worker.id)
                          }
                        }}
                        className="grid w-full cursor-pointer grid-cols-[44px_1.2fr_0.7fr_0.6fr_1.5fr] items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-accent/50"
                      >
                        <div
                          className="flex items-center justify-center"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox checked={selected} onCheckedChange={() => toggleWorker(worker.id)} />
                        </div>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {getInitials(formatWorkerName(worker))}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">{formatWorkerName(worker)}</p>
                            <p className="truncate text-xs text-muted-foreground">{worker.email || worker.phone || 'No contact'}</p>
                          </div>
                        </div>
                        <div className="truncate text-sm text-muted-foreground">{workerRole(worker)}</div>
                        <div className="text-sm text-muted-foreground">
                          {metrics?.assignedJobs.length ?? 0} job{(metrics?.assignedJobs.length ?? 0) === 1 ? '' : 's'}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className={cn('font-medium', availability >= 70 ? 'text-emerald-600' : availability >= 40 ? 'text-amber-600' : 'text-rose-600')}>
                              {availability}% free
                            </span>
                            <span>
                              {metrics ? `${metrics.assignedHours.toFixed(1)}h booked` : 'No timeline data'}
                            </span>
                          </div>
                          <div className="grid grid-cols-24 gap-0.5">
                            {(metrics?.occupiedSlots ?? Array.from({ length: 24 }, () => false)).map((isBusy, index) => (
                              <div
                                key={`${worker.id}-${index}`}
                                className={cn(
                                  'h-2 rounded-sm',
                                  isBusy ? 'bg-amber-500/80' : selected ? 'bg-primary/40' : 'bg-emerald-500/30',
                                )}
                                title={timelineWindow ? `${toTimeLabel(timelineWindow.slots[index].start)} - ${toTimeLabel(timelineWindow.slots[index].end)}: ${isBusy ? 'Assigned' : 'Free'}` : undefined}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {timelineWindow ? (
              <p className="text-xs text-muted-foreground">
                Timeline window: {toTimeLabel(timelineWindow.start)} on {timelineWindow.startDateOnly} to {toTimeLabel(timelineWindow.end)} on {timelineWindow.endDateOnly}.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Select the recurrence start date and time to load a 24-hour assignment timeline.</p>
            )}

            <p className="text-sm text-muted-foreground">
              {totalSelectedWorkers > 0
                ? `${totalSelectedWorkers} worker${totalSelectedWorkers === 1 ? '' : 's'} selected. The first selected worker becomes the primary assignee.`
                : 'Pick workers to prefill the primary assignee.'}
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-border bg-card shadow-sm">
          <CardHeader className="space-y-1 border-b border-border bg-muted/30">
            <CardTitle className="text-base text-foreground">Finalize</CardTitle>
            <CardDescription className="text-muted-foreground">Set the job state and leave instructions for the recurring run.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="on_hold">On hold</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Controller
                  control={control}
                  name="priority"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="priority">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                rows={4}
                placeholder="Add special instructions, access notes, or recurring requirements."
                {...register('instructions')}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 shadow-md">
          <Button type="button" variant="outline" className="border-slate-300 hover:bg-slate-100" onClick={() => navigate(withServiceInstanceId('/job', serviceInstanceId))}>
            Cancel
          </Button>
          <div className="flex flex-col items-end gap-2">
            <Button
              type="submit"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={isSubmitDisabled}
            >
              {createMutation.isPending ? <Spinner className="mr-2" /> : null}
              {createMutation.isPending ? 'Creating recurring job...' : 'Create recurring job'}
            </Button>
            {categoryLoadMessage && isSubmitDisabled ? (
              <p className="text-right text-xs text-muted-foreground">{categoryLoadMessage}</p>
            ) : null}
          </div>
        </div>
      </form>
    </div>
  )
}